import { useMemo } from 'react';
import { LoadingState } from 'components/ui';
import { PrismThemeProvider } from 'theme';
import { OtpRecoveryRequest, OtpRecoveryVerify, OtpResult, OtpSetup, OtpVerification } from './components';
import { useOtpFlow } from './hooks';
import { IDLE_STEP, OTP_COMPLETING_LABEL, OTP_LOADING_LABEL, REQUEST_STAGE, RESULT_STEP, SETUP_STEP, VERIFY_STEP } from './OTP.constants';
import type { OTPProps } from './OTP.props';

const OTP = (props: OTPProps) => {
  const { showActionButtons = true, showAuthHeader, showMandatoryHint = false } = props;
  const {
    backupCodes,
    handleBackupCodesAcknowledge,
    currentStep,
    formState,
    generateBackupCodes,
    hasBackupCodes,
    handleCancelSetup,
    handleCopyToClipboard,
    handleInputBlur,
    handleInputChange,
    handleRecoveryBackToBackupCode,
    handleRequestRecoveryCode,
    handleSubmit,
    handleToggleOtpKey,
    isCodeWorking,
    isCompleting,
    isGeneratingBackupCodes,
    isRecoveryResendCoolingDown,
    isRequestingRecoveryCode,
    isSubmitting,
    logOutUser,
    messageBoxData,
    otpData,
    otpSecretKey,
    recoveryRequestError,
    recoveryResendRemainingSeconds,
    recoveryStage,
    rememberThisComputer,
    returnToOtpVerificationFlow,
    showRememberThisComputer,
    showOtpKey,
    startBackupCodeFlow,
    startRecoveryRequestFlow,
    setRememberThisComputer,
    username,
  } = useOtpFlow(props);

  const content = useMemo(() => {
    if (isCompleting) {
      return <LoadingState label={OTP_COMPLETING_LABEL} />;
    }

    if (currentStep === VERIFY_STEP && isCodeWorking && recoveryStage !== IDLE_STEP) {
      if (recoveryStage === REQUEST_STAGE) {
        return (
          <OtpRecoveryRequest
            errorMessage={recoveryRequestError ?? undefined}
            isSubmitting={isRequestingRecoveryCode}
            showHeader={showAuthHeader}
            onBack={handleRecoveryBackToBackupCode}
            onRequestCode={handleRequestRecoveryCode}
          />
        );
      }

      return (
        <OtpRecoveryVerify
          errorMessage={recoveryRequestError ?? undefined}
          formState={formState}
          isRequestingRecoveryCode={isRequestingRecoveryCode}
          isResendCoolingDown={isRecoveryResendCoolingDown}
          isSubmitting={isSubmitting}
          remainingCooldownSeconds={recoveryResendRemainingSeconds}
          showHeader={showAuthHeader}
          onBack={handleRecoveryBackToBackupCode}
          onInputBlur={handleInputBlur}
          onInputChange={handleInputChange}
          onResendCode={handleRequestRecoveryCode}
          onSubmit={handleSubmit}
        />
      );
    }

    if (currentStep === VERIFY_STEP) {
      return (
        <OtpVerification
          formState={formState}
          isCodeWorking={isCodeWorking}
          isSubmitting={isSubmitting}
          rememberThisComputer={rememberThisComputer}
          showMandatoryHint={showMandatoryHint}
          showRememberThisComputer={showRememberThisComputer}
          showHeader={showAuthHeader}
          showBackupCodeAction={hasBackupCodes}
          onCancel={isCodeWorking ? returnToOtpVerificationFlow : logOutUser}
          onCodeNotWorking={startBackupCodeFlow}
          onInputBlur={handleInputBlur}
          onInputChange={handleInputChange}
          onNoBackupCodesEither={startRecoveryRequestFlow}
          onRememberThisComputerChange={setRememberThisComputer}
          onSubmit={handleSubmit}
        />
      );
    }

    if (currentStep === SETUP_STEP && otpData) {
      return (
        <OtpSetup
          formState={formState}
          otpSecretKey={otpSecretKey}
          otpUrl={otpData.url}
          showHeader={showAuthHeader}
          isSubmitting={isSubmitting}
          showMandatoryHint={showMandatoryHint}
          showOtpKey={showOtpKey}
          onCancel={handleCancelSetup}
          onCopyOtpKey={handleCopyToClipboard}
          onInputBlur={handleInputBlur}
          onInputChange={handleInputChange}
          onSubmit={handleSubmit}
          onToggleOtpKey={handleToggleOtpKey}
        />
      );
    }

    if (currentStep === RESULT_STEP) {
      return (
        <OtpResult
          backupCodes={backupCodes}
          isGeneratingBackupCodes={isGeneratingBackupCodes}
          messageBoxData={messageBoxData}
          onBackupCodesAcknowledge={handleBackupCodesAcknowledge}
          onGenerateBackupCodes={generateBackupCodes}
          showActionButtons={showActionButtons}
          showHeader={showAuthHeader}
          username={username}
        />
      );
    }

    return <LoadingState label={OTP_LOADING_LABEL} />;
  }, [
    backupCodes,
    currentStep,
    formState,
    generateBackupCodes,
    handleBackupCodesAcknowledge,
    handleCancelSetup,
    handleCopyToClipboard,
    handleInputBlur,
    handleInputChange,
    handleRecoveryBackToBackupCode,
    handleRequestRecoveryCode,
    handleSubmit,
    handleToggleOtpKey,
    hasBackupCodes,
    isCodeWorking,
    isCompleting,
    isGeneratingBackupCodes,
    isRecoveryResendCoolingDown,
    isRequestingRecoveryCode,
    isSubmitting,
    logOutUser,
    messageBoxData,
    otpData,
    otpSecretKey,
    recoveryRequestError,
    recoveryResendRemainingSeconds,
    recoveryStage,
    rememberThisComputer,
    returnToOtpVerificationFlow,
    setRememberThisComputer,
    showActionButtons,
    showAuthHeader,
    showMandatoryHint,
    showOtpKey,
    showRememberThisComputer,
    startBackupCodeFlow,
    startRecoveryRequestFlow,
    username,
  ]);

  return <PrismThemeProvider>{content}</PrismThemeProvider>;
};

export default OTP;
